import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Shield,
  Calendar,
  Wallet,
  Crown,
  CheckCircle,
  XCircle,
  ArrowUpRight,
  Loader2,
  CreditCard,
  BarChart3,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_CONFIG = {
  free: { label: "Free", color: "bg-gray-500/15 text-gray-300 border-gray-500/30" },
  beginner: { label: "Starter", color: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  pro: { label: "Pro", color: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  admin: { label: "Admin", color: "bg-red-500/15 text-red-400 border-red-500/30" },
};

export default function Profile() {
  const { user, creditsBalance, userRole } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchProfileData() {
      try {
        const [subRes, usageRes] = await Promise.all([
          axios.get(`${API}/api/subscriptions/status`, { withCredentials: true }).catch(() => null),
          axios.get(`${API}/api/auth/me/usage`, { withCredentials: true }).catch(() => null),
        ]);
        if (subRes?.data?.subscription) setSubscription(subRes.data.subscription);
        if (usageRes?.data) setUsage(usageRes.data);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    }
    fetchProfileData();
  }, []);

  const roleConfig = ROLE_CONFIG[userRole] || ROLE_CONFIG.free;

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="profile-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="profile-page">
      {/* Header */}
      <div className="rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/80 to-primary/40 flex items-center justify-center text-2xl font-bold text-white shrink-0" data-testid="profile-avatar">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div>
            <h1 className="text-2xl font-bold" data-testid="profile-name">{user?.name || "User"}</h1>
            <span className={`inline-block mt-1 px-3 py-0.5 rounded-full text-xs font-semibold border ${roleConfig.color}`} data-testid="profile-role-badge">
              {roleConfig.label}
            </span>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <section className="rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5 space-y-4" data-testid="profile-account-section">
        <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider">Account Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InfoRow icon={User} label="Name" value={user?.name || "Not set"} testId="profile-info-name" />
          <InfoRow icon={Mail} label="Email" value={user?.email} testId="profile-info-email" />
          <InfoRow icon={Shield} label="Auth Provider" value={user?.auth_provider === "google" ? "Google" : "Email"} testId="profile-info-auth" />
          <div className="flex items-center gap-3" data-testid="profile-info-verified">
            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
              {user?.email_verified ? (
                <CheckCircle className="w-4 h-4 text-emerald-400" />
              ) : (
                <XCircle className="w-4 h-4 text-red-400" />
              )}
            </div>
            <div>
              <p className="text-[11px] text-white/40">Email Verified</p>
              <p className={`text-sm font-medium ${user?.email_verified ? "text-emerald-400" : "text-red-400"}`}>
                {user?.email_verified ? "Verified" : "Not verified"}
              </p>
            </div>
          </div>
          <InfoRow icon={Calendar} label="Member Since" value={formatDate(user?.created_at)} testId="profile-info-joined" />
        </div>
      </section>

      {/* Plan & Credits */}
      <section className="rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5 space-y-4" data-testid="profile-plan-section">
        <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider">Plan & Credits</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-lg bg-white/5 border border-white/10 p-4 flex items-center gap-3" data-testid="profile-credits-card">
            <div className="w-10 h-10 rounded-lg bg-orange-500/15 flex items-center justify-center shrink-0">
              <Wallet className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-[11px] text-white/40">Credits Balance</p>
              <p className="text-xl font-bold text-white">{creditsBalance}</p>
            </div>
          </div>
          <div className="rounded-lg bg-white/5 border border-white/10 p-4 flex items-center gap-3" data-testid="profile-plan-card">
            <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
              <Crown className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-[11px] text-white/40">Current Plan</p>
              <p className="text-xl font-bold text-white">{roleConfig.label}</p>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        {subscription && (
          <div className="rounded-lg bg-white/5 border border-white/10 p-4 space-y-2" data-testid="profile-subscription-details">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-white/40" />
              <p className="text-xs text-white/40">Active Subscription</p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-white/40 text-[11px]">Plan</p>
                <p className="text-white font-medium">{subscription.plan_key?.replace(/_/g, " ")?.replace(/\b\w/g, c => c.toUpperCase())}</p>
              </div>
              <div>
                <p className="text-white/40 text-[11px]">Status</p>
                <p className="text-emerald-400 font-medium capitalize">{subscription.status}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="outline" className="border-white/15 text-white/70 hover:text-white hover:bg-white/10" onClick={() => navigate("/credits")} data-testid="profile-topup-btn">
            <Wallet className="w-3.5 h-3.5 mr-1.5" /> Top Up Credits
          </Button>
          {subscription && (subscription.status === "active" || subscription.status === "authenticated") ? (
            <>
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90 text-white"
                onClick={() => navigate("/pricing")}
                data-testid="profile-manage-plan-btn"
              >
                <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" /> Change Plan
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                onClick={() => navigate("/pricing")}
                data-testid="profile-cancel-sub-btn"
              >
                Cancel Subscription
              </Button>
            </>
          ) : (
            userRole !== "pro" && userRole !== "admin" && (
              <Button size="sm" className="bg-primary hover:bg-primary/90 text-white" onClick={() => navigate("/pricing")} data-testid="profile-upgrade-btn">
                <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" /> Upgrade Plan
              </Button>
            )
          )}
        </div>
      </section>

      {/* Usage Summary */}
      {usage?.usage && (
        <section className="rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5 space-y-4" data-testid="profile-usage-section">
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider">Usage This Period</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {usage.usage.video_generations_this_month && (
              <UsageStat icon={Zap} label="Videos This Month" value={usage.usage.video_generations_this_month.used} limit={usage.usage.video_generations_this_month.unlimited ? "\u221e" : usage.usage.video_generations_this_month.limit} testId="profile-usage-videos" />
            )}
            {usage.usage.image_generations_this_month && (
              <UsageStat icon={BarChart3} label="Images This Month" value={usage.usage.image_generations_this_month.used} limit={usage.usage.image_generations_this_month.unlimited ? "\u221e" : usage.usage.image_generations_this_month.limit} testId="profile-usage-images" />
            )}
            {usage.usage.projects && (
              <UsageStat icon={Wallet} label="Projects" value={usage.usage.projects.used} limit={usage.usage.projects.unlimited ? "\u221e" : usage.usage.projects.limit} testId="profile-usage-projects" />
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-white/40" />
      </div>
      <div>
        <p className="text-[11px] text-white/40">{label}</p>
        <p className="text-sm font-medium text-white">{value}</p>
      </div>
    </div>
  );
}

function UsageStat({ icon: Icon, label, value, limit, testId }) {
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-white/30" />
        <p className="text-[11px] text-white/40">{label}</p>
      </div>
      <p className="text-lg font-bold text-white">
        {value}{limit !== undefined && <span className="text-sm text-white/30 font-normal"> / {limit}</span>}
      </p>
    </div>
  );
}
